import { useCallback, useState } from 'react';
import useAnalytics from '../../../../hooks/useAnalytics';
import { EVENT_NAMES } from '../../../constants/eventNames';
import { useChatbotContext } from '../../../hooks/ChatbotContext';
import chatService from '../../../services/chatService';
import messageContentParser from '../../../utils/MessageContentParser';
import { sanitizeMessageText } from '../../../utils/sanitizeMessageText';

/**
 * Custom hook for message-related actions
 * @param {Object} params - Hook parameters
 * @param {Object} params.message - The message object
 * @param {Function} params.onMessageFlagged - Callback when message is flagged successfully
 * @returns {Object} Message action handlers and states
 */
export function useMessageActions({ message, onMessageFlagged }) {
  // Get context data for analytics
  const { audioManager, currentConversationId, mode } = useChatbotContext();
  const { trackEvent } = useAnalytics();

  // Copy functionality
  const [showCopySuccess, setShowCopySuccess] = useState(false);

  // Flag functionality
  const [isFlagModalOpen, setIsFlagModalOpen] = useState(false);
  const [isFlagging, setIsFlagging] = useState(false);
  const [flagStatusModal, setFlagStatusModal] = useState({ open: false, type: 'success' });

  const handleCopyMessage = useCallback(() => {
    const cleanText = sanitizeMessageText(message.text);
    navigator.clipboard.writeText(cleanText).then(() => {
      setShowCopySuccess(true);
      setTimeout(() => setShowCopySuccess(false), 1500);
    });
  }, [message.text]);

  // Audio functionality - simplified with audioManager
  const handlePlayAudio = useCallback(() => {
    audioManager.playAudio(message.id, message.text);
  }, [audioManager, message.id, message.text]);

  // Flag modal handlers
  const handleOpenFlagModal = useCallback(() => {
    setIsFlagModalOpen(true);
  }, []);

  const handleCloseFlagModal = useCallback(() => {
    setIsFlagModalOpen(false);
  }, []);

  const handleCloseFlagStatusModal = useCallback(() => {
    setFlagStatusModal({ open: false, type: 'success' });
  }, []);

  /**
   * Extract content types from message text
   * @param {string} text - Message text to parse
   * @returns {string[]} Array of content types found
   */
  const extractContentTypes = useCallback((text) => {
    if (!text) return ['text'];

    const parsedContent = messageContentParser.parseContent(text);
    const types = parsedContent.map((part) => part.type).filter(Boolean);

    // Ensure we have at least 'text' if no specific types found
    return types.length > 0 ? [...new Set(types)] : ['text'];
  }, []);

  const handleSubmitFlag = useCallback(
    async ({ reason, comment }) => {
      setIsFlagging(true);

      try {
        const response = await chatService.flagMessage(message.id, reason, comment);

        // Close flag modal
        setIsFlagModalOpen(false);

        // Show success status modal
        setFlagStatusModal({ open: true, type: 'success' });

        // Track tutor error reported event (non-blocking)
        try {
          trackEvent(EVENT_NAMES.TUTOR_ERROR_REPORTED, {
            message_id: message.id,
            conversation_id: currentConversationId,
            mode: mode,
            content_format: extractContentTypes(message.text),
            report_reason: reason,
          });
        } catch (analyticsError) {
          console.error('Failed to track tutor error reported event:', analyticsError);
        }

        // Notify parent component that message was flagged
        if (onMessageFlagged) {
          onMessageFlagged(message.id, response);
        }
      } catch (error) {
        console.error('Error flagging message:', error);

        // Close flag modal
        setIsFlagModalOpen(false);

        // Show error status modal
        setFlagStatusModal({ open: true, type: 'error' });
      } finally {
        setIsFlagging(false);
      }
    },
    [message.id, message.text, onMessageFlagged, trackEvent, currentConversationId, mode, extractContentTypes]
  );

  return {
    // Copy actions
    handleCopyMessage,
    showCopySuccess,

    // Audio actions
    handlePlayAudio,
    isPlayingAudio: audioManager.isPlaying(message.id),
    isLoadingAudio: audioManager.isLoading(message.id),
    audioError: audioManager.error,

    // Flag actions
    isFlagModalOpen,
    isFlagging,
    flagStatusModal,
    handleOpenFlagModal,
    handleCloseFlagModal,
    handleCloseFlagStatusModal,
    handleSubmitFlag,
  };
}
